import { themeColors } from "../themeColors";
import BackButton from "@/components/Common/BackButton";
/**
 * @file   src\pages\ViewClientDetails.js
 * @brief  This file is responsible for view client details.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Tabs, Tab, Badge } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import {
  deleteClient,
  deleteClientNote,
  getClientDetails,
  getClientNotes,
  saveAgentClient,
  saveClientNote,
} from "../services/agentClientsService";
import ClientNotesModal from "../components/AgentDashboard/ClientNotesModal";
import { getLocalStorage, getUserId, setLocalStorage } from "../helpers/common";
import {
  CLIENT_DETAILS_KEY,
  DRAFT,
  INVENTORY_KEY,
  PAUSED,
  PUBLISHED,
  USER_INFO_KEY,
} from "../constants/common";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import ViewClientWantAds from "../components/AgentDashboard/v2/ViewClientWantAds";
import ViewClientInventory from "../components/AgentDashboard/ViewClientInventory";
import EditClientModal from "../components/AgentDashboard/EditClientModal";
import {
  archiveWantAd,
  deleteWantAd,
  getWantAdById,
  unArchiveWantAd,
} from "../services/wantAdService";
import ArchiveModal from "../components/MyWantAd/ArchiveModal";
import DeleteModal from "../components/Common/DeleteModal";
import { toast } from "react-toastify";
import {
  deleteMyInventory,
  getInventoryById,
  inventoryMarkAsSold,
} from "../services/inventoryService";
import _ from "lodash";
import ClientDeleteModal from "../components/AgentDashboard/ClientDeleteModal";
import PremiumPlanModal from "../components/PlanUpgrade/PremiumPlanModal";
import { fetchUserDetails } from "../actions/userprofile";
import {
  getUserDetails,
  getUserStatistics,
} from "../services/userprofileService";
import { useDispatch, useSelector } from "react-redux";
import { PageWrapper } from "@/components/PageWrapper";

export const ClientName = ({ name }) =>
  String(name || "").trim() ? (
    name
  ) : (
    <span
      className="client-name-placeholder"
      aria-hidden="true"
      style={{ display: "inline-block", width: "12rem", height: "1em", borderRadius: 6, background: "var(--neutral-200, #eceef1)", verticalAlign: "middle" }}
    />
  );

const USER_DETAILS_KEY = "userDetails";
const USER_STATISTICS_KEY = "userStatistics";
const CLIENT_DETAILS_QUERY_KEY = "viewClientDetails";

const ViewClientDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const WANT_AD_TAB = "WANT_AD_TAB";
  const INVENTORY_TAB = "INVENTORY_TAB";
  const [clientNotes, setClientNotes] = useState([]);
  const [status, setStatus] = useState(PUBLISHED);
  const [activeParentTab, setParentTab] = useState(WANT_AD_TAB);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [deleteClientModal, setDeleteClientModal] = useState(false);
  const [itemId, setItemId] = useState(0);
  const [archiveModalShow, setArchiveModalShow] = useState(false);
  const [showEditModal, setEditModal] = useState(false);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const PAGE_LIMIT = 10;
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);
  const userId = getUserId();
  const queryClient = useQueryClient();

  const userPrivileges = useSelector(
    (state) => state?.userProfile?.userPrivileges,
  );

  const userDetailsQuery = useQuery({
    queryKey: [USER_DETAILS_KEY],
    queryFn: () => getUserDetails(),
    enabled: !!userId,
  });
  const userDetails = useMemo(
    () => _.get(userDetailsQuery.data, "result[0]", null) || null,
    [userDetailsQuery.data],
  );

  // Mirror the fetched user details into Redux so the shared PremiumPlanModal,
  // which reads state.userProfile.userDetails to derive the active plan, keeps
  // working without issuing a second network request.
  useEffect(() => {
    if (userDetailsQuery.data) {
      dispatch(fetchUserDetails.fulfilled(userDetailsQuery.data, "rq-mirror"));
    }
  }, [userDetailsQuery.data, dispatch]);

  const userStatisticsQuery = useQuery({
    queryKey: [USER_STATISTICS_KEY, userDetails?.User_Plan_Id],
    queryFn: () => getUserStatistics({ UserPlanId: userDetails?.User_Plan_Id }),
    enabled: !!userDetails,
  });
  const userCount = useMemo(
    () => _.get(userStatisticsQuery.data, "result[0]", {}) || {},
    [userStatisticsQuery.data],
  );
  const allowedCount = useMemo(
    () =>
      userPrivileges !== null && userPrivileges?.length > 0
        ? userPrivileges[0]
        : {},
    [userPrivileges],
  );

  const updateReloadParam = () => {
    queryClient.invalidateQueries({ queryKey: [USER_DETAILS_KEY] });
    queryClient.invalidateQueries({ queryKey: [USER_STATISTICS_KEY] });
  };

  const storedClientId = _.get(getLocalStorage(CLIENT_DETAILS_KEY), "id", "");

  const clientDetailsQuery = useInfiniteQuery({
    queryKey: [
      CLIENT_DETAILS_QUERY_KEY,
      storedClientId,
      activeParentTab,
      status,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getClientDetails({
        clientId: storedClientId,
        isClientInfoReq: true,
        type: activeParentTab === WANT_AD_TAB ? 1 : 2,
        sortBy: "publishedon",
        page: pageParam,
        limit: PAGE_LIMIT,
        status: status,
      });
      const responseResult = _.get(response, "result", null);
      if (response.errorCode !== 0 || !responseResult) {
        throw new Error("Failed to fetch client details");
      }
      return { ...responseResult, page: pageParam };
    },
    getNextPageParam: (lastPage) => {
      const items =
        activeParentTab === WANT_AD_TAB
          ? _.get(lastPage, "wantAdList", [])
          : _.get(lastPage, "inventoryList", []);
      return items.length === PAGE_LIMIT ? lastPage.page + 1 : undefined;
    },
    enabled: !!storedClientId,
  });

  const clientDetailsPages = clientDetailsQuery.data?.pages ?? [];
  // The latest page carries the client header info and the per-tab counts.
  const clientDetails = clientDetailsPages.length
    ? clientDetailsPages[clientDetailsPages.length - 1]
    : "";

  const activeList = useMemo(
    () =>
      (clientDetailsQuery.data?.pages ?? []).flatMap((p) =>
        activeParentTab === WANT_AD_TAB
          ? _.get(p, "wantAdList", [])
          : _.get(p, "inventoryList", []),
      ),
    [clientDetailsQuery.data, activeParentTab],
  );

  // The render still references three status-specific lists; only the active
  // status tab is populated, mirroring the original reset-on-tab-switch flow.
  const publishedList = status === PUBLISHED ? activeList : [];
  const pausedList = status === PAUSED ? activeList : [];
  const draftList = status === DRAFT ? activeList : [];
  // While a fetch is in flight we keep the list branch visible (as the original
  // did) so the empty-state copy does not flash before results arrive.
  const showNoResult = clientDetailsQuery.isFetching;

  const archiveWantAdMutation = useMutation({ mutationFn: archiveWantAd });
  const unArchiveWantAdMutation = useMutation({ mutationFn: unArchiveWantAd });
  const saveClientMutation = useMutation({ mutationFn: saveAgentClient });
  const deleteInventoryMutation = useMutation({ mutationFn: deleteMyInventory });
  const deleteWantAdMutation = useMutation({ mutationFn: deleteWantAd });
  const deleteClientMutation = useMutation({ mutationFn: deleteClient });
  const saveNoteMutation = useMutation({ mutationFn: saveClientNote });
  const deleteNoteMutation = useMutation({ mutationFn: deleteClientNote });

  const refreshClientDetails = () =>
    queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });

  const loadClientNotes = async (page, isListUpdated) => {
    const clientId = _.get(getLocalStorage(CLIENT_DETAILS_KEY), "id", "");
    const reqPayload = {
      clientId: clientId,
      page: page,
      limit: PAGE_LIMIT,
    };

    await getClientNotes(reqPayload)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0) {
          if (isListUpdated) {
            setClientNotes(responseResult);
          } else {
            setClientNotes([...clientNotes, ...responseResult]);
          }
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  /**
   * To handle scroll for pagination
   */
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = clientDetailsQuery;
  const handleScroll = useCallback(() => {
    let userScrollHeight = window.innerHeight + window.scrollY;
    let windowBottomHeight = document.documentElement.offsetHeight;

    if (
      userScrollHeight >= windowBottomHeight &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const onEdit = (wantAdId) => {
    getWantAdDetails(wantAdId, { isEdit: true });
  };

  /**
   * get want-ad details for edit
   * @param {*} wantAdId
   */
  const getWantAdDetails = async (wantAdId, formData) => {
    if (wantAdId) {
      await getWantAdById(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (responseResult.isAgentCreated === 1) {
              navigate(`${pageURLs.editWantAdForAgents}/${responseResult.wantAdId}`, {
                state: { data: responseResult, ...formData },
              });
            } else {
              navigate(pageURLs.agentEditwantad, {
                state: { data: responseResult },
              });
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      navigate(pageURLs.agentCreatewantad);
    }
  };
  /**
   * To handle edit inventory
   * @param {*} invId
   * @param {*} isDraftItem
   */
  const onInventoryEdit = (invId, isDraftItem) => {
    if (isDraftItem && userCount?.Inventory >= allowedCount?.Inventory) {
      PremiumPlanModalShow();
    } else {
      getInventoryDetails(invId);
    }
  };

  const getInventoryDetails = async (inventoryId) => {
    if (inventoryId) {
      await getInventoryById(inventoryId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (responseResult.isAgentCreated === 1) {
              navigate(pageURLs.agentCreateInventory, {
                state: {
                  data: responseResult,
                  inventoryId: responseResult.inventoryId,
                },
              });
            } else {
              navigate(pageURLs.memberEditInventory, {
                state: { data: responseResult },
              });
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      navigate(pageURLs.agentCreatewantad);
    }
  };
  /**
   * To handle inventory delete
   * @param {*} inventryId
   */
  const onInventoryDelete = (inventryId) => {
    setItemId(inventryId);
    setDeleteModalShow(true);
  };

  /**
   * inventory  mark as sold button click action
   */
  const onMarkAsSold = (inventryId) => {
    handleInventoryMarkAsSold(inventryId);
  };

  /**
   * To handle the inventory mark as sold
   */
  const MarkAsSoldSuccess = () => {
    refreshClientDetails();
  };

  const onWantAdArchived = (id) => {
    setArchiveModalShow(true);
    setItemId(id);
  };
  /**
   * To handle pause want ad
   */
  const handleArchiveWantAd = async () => {
    if (itemId) {
      await archiveWantAdMutation
        .mutateAsync(itemId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            toast.success("Your want-listing has been paused.");
            setArchiveModalShow(false);
            refreshClientDetails();
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  const handleSaveClientDetails = async (reqPayload) => {
    await saveClientMutation
      .mutateAsync(reqPayload)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          const userInfo = getLocalStorage(USER_INFO_KEY);
          userInfo.userInfo["ClientCount"] = userInfo.userInfo?.ClientCount
            ? userInfo.userInfo?.ClientCount + 1
            : 1;
          setLocalStorage(USER_INFO_KEY, userInfo);

          toast.success("Client updated.");
          setEditModal(false);
          refreshClientDetails();
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const addClientNotes = async (reqPayload) => {
    return await saveNoteMutation
      .mutateAsync(reqPayload)
      .then((response) => {
        if (response.errorCode === 0) {
          toast.success("Note added.");
          return response;
        } else {
          console.log("Something went wrong");
          return "";
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const deleteClientNotes = async (reqPayload) => {
    return await deleteNoteMutation
      .mutateAsync(reqPayload)
      .then((response) => {
        if (response.errorCode === 0) {
          toast.success("Note deleted.");
          return response;
        } else {
          console.log("Something went wrong");
          return "";
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleUnArchiveWantAd = async (wantAd) => {
    if (wantAd?.Want_ad_Id) {
      if (
        (wantAd?.Buy_or_Rent == 2 && wantAd?.Move_in_TimeLine == 11) ||
        wantAd?.Buy_or_Rent == 3
      ) {
        getWantAdDetails(wantAd?.Want_ad_Id, { isFromUnPause: true });
      } else {
        await unArchiveWantAdMutation
          .mutateAsync(wantAd?.Want_ad_Id)
          .then((response) => {
            if (response.errorCode == 0) {
              toast.success(
                "Your paused want-listing has been made public again.",
              );
              refreshClientDetails();
            } else {
              console.log("Something went wrong");
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      }
    }
  };

  const onWantAdDelete = (id) => {
    setDeleteModalShow(true);
    setItemId(id);
  };

  /**
   * To handle the want-ad/Inventory delete
   */

  const handleDeleteItem = async () => {
    if (itemId) {
      if (activeParentTab === INVENTORY_TAB) {
        await deleteInventoryMutation
          .mutateAsync(itemId)
          .then((response) => {
            const responseResult = _.get(response, "result", null);
            if (response.errorCode === 0 && responseResult) {
              toast.success("Your inventory has been successfully deleted.");
              setDeleteModalShow(false);
              refreshClientDetails();
            } else {
              console.log("Something went wrong");
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      } else {
        await deleteWantAdMutation
          .mutateAsync(itemId)
          .then((response) => {
            const responseResult = _.get(response, "result", null);
            if (response.errorCode === 0 && responseResult) {
              toast.success("Your Want-Listing has been successfully deleted.");
              setDeleteModalShow(false);
              refreshClientDetails();
            } else {
              console.log("Something went wrong");
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      }
    }
  };

  const handleDeleteClient = async () => {
    const clientId = _.get(clientDetails, "clientDetails.ClientId", "");
    if (clientId) {
      await deleteClientMutation
        .mutateAsync(clientId)
        .then((response) => {
          if (response.errorCode === 0) {
            toast.success("Client deleted.");
            setDeleteClientModal(false);
            navigate(pageURLs.agentDashboard);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  const clientFirstName = _.get(clientDetails, "clientDetails.FirstName", "");
  const clientLastName = _.get(clientDetails, "clientDetails.LastName", "");
  const clientId = _.get(clientDetails, "clientDetails.ClientId", "");
  const clientFullName = clientFirstName + " " + clientLastName;
  const clientBio = _.get(clientDetails, "clientDetails.Add_Info", "");
  let deleteButtonProps = {};

  if (activeParentTab === INVENTORY_TAB) {
    _.set(
      deleteButtonProps,
      "title",
      "Are you sure you want to permanently delete your property?",
    );
    _.set(
      deleteButtonProps,
      "message",
      "Deleting this property from your inventory will remove any pending and accepted introductions sent by you.",
    );
    _.set(deleteButtonProps, "btnText", " Delete Property");
  } else if (activeParentTab === WANT_AD_TAB) {
    _.set(
      deleteButtonProps,
      "title",
      "Are you sure you want to permanently delete your want-listing?",
    );
    _.set(
      deleteButtonProps,
      "message",
      "Deleting this listing will stop any further introduction from being sent to you.",
    );
    _.set(deleteButtonProps, "btnText", " Delete Want-Listing");
  }

  const handleTabChange = (tab) => {
    if (tab !== status && tab !== activeParentTab) {
      if (tab === PUBLISHED || tab === DRAFT || tab === PAUSED) {
        setStatus(tab);
      } else {
        setStatus(PUBLISHED);
        setParentTab(tab);
      }
    }
  };

  const handleAddProperty = () => {
    if (userCount?.Inventory >= allowedCount?.Inventory) {
      PremiumPlanModalShow();
    } else {
      navigate(pageURLs.agentCreateInventory, {
        state: {
          data: {
            clientId: clientId,
            clientName: clientFullName,
          },
        },
      });
    }
  };

  const handleInventoryItemClick = (inventoryId) => {
    navigate(`${pageURLs.inventoryDetails}/${inventoryId}`, {
      state: { isFromListingPage: true, isFromAgentDashboard: true },
    });
  };

  function LoadCreateItemTile(props) {
    const { showNoResult, list } = props;
    if (showNoResult || (list !== null && list?.length > 0)) {
      return (
        <div className="col-md-4 mb-4">
          <button
            className="btn create-wantad-btn h-306"
            onClick={() =>
              activeParentTab === WANT_AD_TAB
                ? navigate(pageURLs.agentCreatewantad, {
                  state: {
                    data: {
                      clientId: clientId,
                      clientName: clientFullName,
                      isEdit: false,
                    },
                  },
                })
                : handleAddProperty()
            }
          >
            <span>
              <svg
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.91669 7C1.91669 6.75838 2.11256 6.5625 2.35419 6.5625H11.9792C12.2208 6.5625 12.4167 6.75838 12.4167 7C12.4167 7.24162 12.2208 7.4375 11.9792 7.4375H2.35419C2.11256 7.4375 1.91669 7.24162 1.91669 7Z"
                  fill={themeColors.primary500}
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.16669 1.75C7.40831 1.75 7.60419 1.94588 7.60419 2.1875V11.8125C7.60419 12.0541 7.40831 12.25 7.16669 12.25C6.92506 12.25 6.72919 12.0541 6.72919 11.8125V2.1875C6.72919 1.94588 6.92506 1.75 7.16669 1.75Z"
                  fill={themeColors.primary500}
                />
              </svg>
            </span>{" "}
            {activeParentTab === WANT_AD_TAB
              ? "Create want-listing"
              : "Add Property"}
          </button>
        </div>
      );
    }
  }

  return (
    <>
      <PageWrapper>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-3">
              <div className="col-md-12">
                <div className="my-want-ad-wrap">
                  <div className="want-ad-agent-info">
                    <div className="row m-0">
                      <div className="col-12 col-md-6 p-0">
                        <div>
                          <BackButton className="mb-24 d-inline-block" />
                        </div>
                        <div>
                          <h2 className="d-inline-block align-middle mx-1">
                            <ClientName name={clientFullName} />
                          </h2>
                          <Dropdown className="d-inline-block align-middle more-icon-vertical ms-2">
                            <Dropdown.Toggle
                              id="dropdown-basic"
                              variant=""
                              align="end"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M14 12.5C14 11.3954 13.1046 10.5 12 10.5C10.8954 10.5 10 11.3954 10 12.5C10 13.6046 10.8954 14.5 12 14.5C13.1046 14.5 14 13.6046 14 12.5Z"
                                  fill={themeColors.neutral780}
                                />
                                <path
                                  d="M14 7C14 5.89543 13.1046 5 12 5C10.8954 5 10 5.89543 10 7C10 8.10457 10.8954 9 12 9C13.1046 9 14 8.10457 14 7Z"
                                  fill={themeColors.neutral780}
                                />
                                <path
                                  d="M14 18C14 16.8954 13.1046 16 12 16C10.8954 16 10 16.8954 10 18C10 19.1046 10.8954 20 12 20C13.1046 20 14 19.1046 14 18Z"
                                  fill={themeColors.neutral780}
                                />
                              </svg>
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              className="dpdown-menu-style"
                              align={"start"}
                            >
                              <Dropdown.Item onClick={() => setEditModal(true)}>
                                <span>
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M14.5581 2.05806C14.8021 1.81398 15.1979 1.81398 15.4419 2.05806L17.9419 4.55806C18.186 4.80214 18.186 5.19786 17.9419 5.44194L10.4419 12.9419C10.3247 13.0592 10.1658 13.125 10 13.125H7.5C7.15482 13.125 6.875 12.8452 6.875 12.5V10C6.875 9.83424 6.94085 9.67527 7.05806 9.55806L14.5581 2.05806ZM8.125 10.2589V11.875H9.74112L16.6161 5L15 3.38388L8.125 10.2589Z"
                                      fill={themeColors.neutral780}
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12.6831 3.93306C12.9271 3.68898 13.3229 3.68898 13.5669 3.93306L16.0669 6.43306C16.311 6.67714 16.311 7.07286 16.0669 7.31694C15.8229 7.56102 15.4271 7.56102 15.1831 7.31694L12.6831 4.81694C12.439 4.57286 12.439 4.17714 12.6831 3.93306Z"
                                      fill={themeColors.neutral780}
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M2.86612 2.86612C3.10054 2.6317 3.41848 2.5 3.75 2.5H10.625C10.9702 2.5 11.25 2.77982 11.25 3.125C11.25 3.47018 10.9702 3.75 10.625 3.75L3.75 3.75L3.75 16.25H16.25V9.375C16.25 9.02982 16.5298 8.75 16.875 8.75C17.2202 8.75 17.5 9.02982 17.5 9.375V16.25C17.5 16.5815 17.3683 16.8995 17.1339 17.1339C16.8995 17.3683 16.5815 17.5 16.25 17.5H3.75C3.41848 17.5 3.10054 17.3683 2.86612 17.1339C2.6317 16.8995 2.5 16.5815 2.5 16.25V3.75C2.5 3.41848 2.6317 3.10054 2.86612 2.86612Z"
                                      fill={themeColors.neutral780}
                                    />
                                  </svg>
                                </span>
                                Edit Client
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => setDeleteClientModal(true)}
                              >
                                <span>
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M9.99979 1.6665C8.53799 1.6665 7.32079 2.75877 7.11649 4.1665H4.26574C4.23025 4.16043 4.19431 4.15744 4.15832 4.15755C4.12721 4.15822 4.0962 4.16122 4.06554 4.1665H2.70812C2.6253 4.16533 2.54307 4.18063 2.46622 4.21152C2.38936 4.2424 2.31941 4.28825 2.26043 4.3464C2.20145 4.40456 2.15462 4.47385 2.12265 4.55026C2.09068 4.62667 2.07422 4.70868 2.07422 4.7915C2.07422 4.87433 2.09068 4.95633 2.12265 5.03275C2.15462 5.10916 2.20145 5.17845 2.26043 5.2366C2.31941 5.29476 2.38936 5.34061 2.46622 5.37149C2.54307 5.40237 2.6253 5.41768 2.70812 5.4165H3.59923L4.64822 16.262C4.76117 17.4315 5.7547 18.3332 6.92931 18.3332H13.0694C14.2441 18.3332 15.2376 17.4316 15.3505 16.262L16.4003 5.4165H17.2915C17.3743 5.41768 17.4565 5.40237 17.5334 5.37149C17.6102 5.34061 17.6802 5.29476 17.7391 5.2366C17.7981 5.17845 17.845 5.10916 17.8769 5.03275C17.9089 4.95633 17.9254 4.87433 17.9254 4.7915C17.9254 4.70868 17.9089 4.62667 17.8769 4.55026C17.845 4.47385 17.7981 4.40456 17.7391 4.3464C17.6802 4.28825 17.6102 4.2424 17.5334 4.21152C17.4565 4.18063 17.3743 4.16533 17.2915 4.1665H15.9348C15.8685 4.15575 15.8009 4.15575 15.7346 4.1665H12.8831C12.6788 2.75877 11.4616 1.6665 9.99979 1.6665ZM9.99979 2.9165C10.7828 2.9165 11.4251 3.4449 11.6087 4.1665H8.3909C8.57447 3.4449 9.2168 2.9165 9.99979 2.9165ZM4.85412 5.4165H15.1446L14.1062 16.1416C14.0541 16.6812 13.6114 17.0832 13.0694 17.0832H6.92931C6.38809 17.0832 5.94458 16.6805 5.89253 16.1416L4.85412 5.4165ZM8.53169 7.49089C8.36607 7.49347 8.20825 7.56169 8.09289 7.68056C7.97754 7.79942 7.91408 7.95922 7.91645 8.12484V14.3748C7.91528 14.4577 7.93058 14.5399 7.96147 14.6167C7.99235 14.6936 8.0382 14.7635 8.09635 14.8225C8.15451 14.8815 8.2238 14.9283 8.30021 14.9603C8.37662 14.9923 8.45862 15.0087 8.54145 15.0087C8.62428 15.0087 8.70628 14.9923 8.78269 14.9603C8.85911 14.9283 8.9284 14.8815 8.98655 14.8225C9.04471 14.7635 9.09056 14.6936 9.12144 14.6167C9.15232 14.5399 9.16762 14.4577 9.16645 14.3748V8.12484C9.16765 8.04119 9.15205 7.95814 9.12056 7.88063C9.08908 7.80312 9.04235 7.73272 8.98316 7.6736C8.92396 7.61448 8.8535 7.56785 8.77595 7.53646C8.6984 7.50508 8.61534 7.48958 8.53169 7.49089ZM11.4484 7.49089C11.2827 7.49347 11.1249 7.56169 11.0096 7.68056C10.8942 7.79942 10.8307 7.95922 10.8331 8.12484V14.3748C10.8319 14.4577 10.8472 14.5399 10.8781 14.6167C10.909 14.6936 10.9549 14.7635 11.013 14.8225C11.0712 14.8815 11.1405 14.9283 11.2169 14.9603C11.2933 14.9923 11.3753 15.0087 11.4581 15.0087C11.5409 15.0087 11.623 14.9923 11.6994 14.9603C11.7758 14.9283 11.8451 14.8815 11.9032 14.8225C11.9614 14.7635 12.0072 14.6936 12.0381 14.6167C12.069 14.5399 12.0843 14.4577 12.0831 14.3748V8.12484C12.0843 8.04119 12.0687 7.95814 12.0372 7.88063C12.0057 7.80312 11.959 7.73272 11.8998 7.6736C11.8406 7.61448 11.7702 7.56785 11.6926 7.53646C11.6151 7.50508 11.532 7.48958 11.4484 7.49089Z"
                                      fill={themeColors.neutral780}
                                    />
                                  </svg>
                                </span>
                                Delete Client
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>

                        <div className="client-bio-parawrap mt-2">
                          <p className="mb-0 ow">{clientBio}</p>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 text-end">
                        <button
                          variant="tertiary"
                          className="btn btn-tertiary"
                          onClick={handleShow}
                        >
                          Client Notes
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="horizontal-tab-wrap position-relative">
                    {activeParentTab === WANT_AD_TAB &&
                      publishedList &&
                      publishedList.length > 2 && (
                        <div className="post-btn-wrap top-94">
                          <button
                            className="btn btn-tertiary d-flex align-items-center"
                            onClick={() =>
                              navigate(pageURLs.agentCreatewantadMainScreen)
                            }
                          >
                            Post{" "}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                            >
                              <g transform="translate(-2 -2)">
                                <path
                                  d="M2,8a.5.5,0,0,1,.5-.5h11a.5.5,0,0,1,0,1H2.5A.5.5,0,0,1,2,8Z"
                                  fill="#000"
                                  fillRule="evenodd"
                                />
                                <path
                                  d="M8,2a.5.5,0,0,1,.5.5v11a.5.5,0,0,1-1,0V2.5A.5.5,0,0,1,8,2Z"
                                  fill="#000"
                                  fillRule="evenodd"
                                />
                              </g>
                            </svg>
                          </button>
                        </div>
                      )}
                    <Tabs
                      id="controlled-tab-example"
                      className="mb-0"
                      unmountOnExit
                      activeKey={activeParentTab}
                      onSelect={(k) => {
                        handleTabChange(k);
                      }}
                    >
                      <Tab
                        eventKey={WANT_AD_TAB}
                        title={<React.Fragment>Want-Listings</React.Fragment>}
                      >
                        <Tabs
                          className="mb-0"
                          id="controlled-tab-example"
                          activeKey={status}
                          onSelect={(k) => {
                            handleTabChange(k);
                          }}
                        >
                          <Tab
                            eventKey={PUBLISHED}
                            title={
                              <React.Fragment>
                                Published
                                <Badge>
                                  {_.get(
                                    clientDetails,
                                    "wantAdPublishedCount",
                                    "0",
                                  )}
                                </Badge>
                              </React.Fragment>
                            }
                          >
                            <div className="row">
                              {showNoResult ||
                                (publishedList !== null &&
                                  publishedList?.length > 0)
                                ? publishedList?.map((wantAd, i) => {
                                  return (
                                    <ViewClientWantAds
                                      key={i}
                                      wantAd={wantAd}
                                      status={status}
                                      onEdit={onEdit}
                                      onWantAdArchived={onWantAdArchived}
                                      handleUnArchiveWantAd={
                                        handleUnArchiveWantAd
                                      }
                                      onWantAdDelete={onWantAdDelete}
                                    />
                                  );
                                })
                                : !showNoResult && (
                                  <div className="no-wantad-wrap">
                                    <h6>
                                      No want listings created yet for this
                                      client.{" "}
                                    </h6>
                                    <p className="mb-4">
                                      To get started creating want listings
                                      use the button below.
                                    </p>
                                    <button
                                      type="button"
                                      className="btn-btn-tweak btn btn-primary"
                                      onClick={() =>
                                        navigate(
                                          pageURLs.agentCreatewantadMainScreen,
                                        )
                                      }
                                    >
                                      Create a want-listing
                                    </button>
                                  </div>
                                )}
                              {activeParentTab === WANT_AD_TAB &&
                                publishedList &&
                                publishedList.length > 2 ? (
                                <></>
                              ) : (
                                <LoadCreateItemTile
                                  showNoResult={showNoResult}
                                  list={publishedList}
                                />
                              )}
                            </div>
                          </Tab>

                          <Tab
                            eventKey={PAUSED}
                            title={
                              <React.Fragment>
                                Paused
                                <Badge>
                                  {_.get(
                                    clientDetails,
                                    "wantAdPausedCount",
                                    "0",
                                  )}
                                </Badge>
                              </React.Fragment>
                            }
                          >
                            <div className="row">
                              {showNoResult ||
                                (pausedList !== null && pausedList?.length > 0)
                                ? pausedList?.map((wantAd, i) => {
                                  return (
                                    <ViewClientWantAds
                                      key={i}
                                      wantAd={wantAd}
                                      status={status}
                                      onEdit={onEdit}
                                      onWantAdArchived={onWantAdArchived}
                                      handleUnArchiveWantAd={
                                        handleUnArchiveWantAd
                                      }
                                      onWantAdDelete={onWantAdDelete}
                                    />
                                  );
                                })
                                : !showNoResult && (
                                  <div className="no-wantad-wrap">
                                    <h6>
                                      No want listings created yet for this
                                      client.{" "}
                                    </h6>
                                    <p className="mb-4">
                                      To get started creating want listings
                                      use the button below.
                                    </p>
                                    <button
                                      type="button"
                                      className="btn-btn-tweak btn btn-primary"
                                      onClick={() =>
                                        navigate(
                                          pageURLs.agentCreatewantadMainScreen,
                                        )
                                      }
                                    >
                                      Create a want-listing
                                    </button>
                                  </div>
                                )}
                              {activeParentTab === WANT_AD_TAB &&
                                publishedList &&
                                publishedList.length > 2 ? (
                                <></>
                              ) : (
                                <LoadCreateItemTile
                                  showNoResult={showNoResult}
                                  list={publishedList}
                                />
                              )}
                            </div>
                          </Tab>

                          <Tab
                            eventKey={DRAFT}
                            title={
                              <React.Fragment>
                                Drafts
                                <Badge>
                                  {_.get(
                                    clientDetails,
                                    "wantAdDraftCount",
                                    "0",
                                  )}
                                </Badge>
                              </React.Fragment>
                            }
                          >
                            <div className="row">
                              {showNoResult ||
                                (draftList !== null && draftList?.length > 0)
                                ? draftList?.map((wantAd, i) => {
                                  return (
                                    <ViewClientWantAds
                                      key={i}
                                      wantAd={wantAd}
                                      status={status}
                                      onEdit={onEdit}
                                      onWantAdArchived={onWantAdArchived}
                                      handleUnArchiveWantAd={
                                        handleUnArchiveWantAd
                                      }
                                      onWantAdDelete={onWantAdDelete}
                                    />
                                  );
                                })
                                : !showNoResult && (
                                  <div className="no-result-wrap mw-435">
                                    <h3>No results found</h3>
                                  </div>
                                )}
                              <LoadCreateItemTile
                                showNoResult={showNoResult}
                                list={draftList}
                              />
                            </div>
                          </Tab>
                        </Tabs>
                      </Tab>

                      <Tab
                        eventKey={INVENTORY_TAB}
                        title={<React.Fragment>Inventory</React.Fragment>}
                      >
                        <Tabs
                          className="mb-0"
                          id="controlled-tab-example"
                          activeKey={status}
                          onSelect={(k) => {
                            handleTabChange(k);
                          }}
                        >
                          <Tab
                            eventKey={PUBLISHED}
                            title={
                              <React.Fragment>
                                Stored
                                <Badge>
                                  {_.get(
                                    clientDetails,
                                    "InventoryPublishedCount",
                                    "0",
                                  )}
                                </Badge>
                              </React.Fragment>
                            }
                          >
                            <div className="row">
                              {showNoResult ||
                                (publishedList !== null &&
                                  publishedList?.length > 0)
                                ? publishedList?.map((inventory, i) => {
                                  return (
                                    <ViewClientInventory
                                      key={i}
                                      inventory={inventory}
                                      status={status}
                                      onEdit={onInventoryEdit}
                                      MarkAsSoldSuccess={MarkAsSoldSuccess}
                                      onInventoryDelete={onInventoryDelete}
                                      handleInventoryItemClick={
                                        handleInventoryItemClick
                                      }
                                    />
                                  );
                                })
                                : !showNoResult && (
                                  <div className="no-wantad-wrap">
                                    <h6>
                                      No inventory added yet for this client.{" "}
                                    </h6>
                                    <p className="mb-4">
                                      To get started adding inventory use the
                                      button below.
                                    </p>
                                    <button
                                      type="button"
                                      className="btn-btn-tweak btn btn-primary"
                                      onClick={() =>
                                        navigate(
                                          pageURLs.agentCreateInventory,
                                          {
                                            state: {
                                              data: {
                                                clientId: clientId,
                                                clientName: clientFullName,
                                              },
                                            },
                                          },
                                        )
                                      }
                                    >
                                      Add property
                                    </button>
                                  </div>
                                )}
                              <LoadCreateItemTile
                                showNoResult={showNoResult}
                                list={publishedList}
                              />
                            </div>
                          </Tab>

                          <Tab
                            eventKey={DRAFT}
                            title={
                              <React.Fragment>
                                Drafts
                                <Badge>
                                  {_.get(
                                    clientDetails,
                                    "InventoryDraftCount",
                                    "0",
                                  )}
                                </Badge>
                              </React.Fragment>
                            }
                          >
                            <div className="row">
                              {showNoResult ||
                                (draftList !== null && draftList?.length > 0)
                                ? draftList?.map((inventory, i) => {
                                  return (
                                    <ViewClientInventory
                                      key={i}
                                      inventory={inventory}
                                      status={status}
                                      onEdit={onInventoryEdit}
                                      onMarkAsSold={onMarkAsSold}
                                      onInventoryDelete={onInventoryDelete}
                                      handleInventoryItemClick={
                                        handleInventoryItemClick
                                      }
                                    />
                                  );
                                })
                                : !showNoResult && (
                                  <div className="no-result-wrap mw-435">
                                    <h3>No results found</h3>
                                  </div>
                                )}
                              <LoadCreateItemTile
                                showNoResult={showNoResult}
                                list={draftList}
                              />
                            </div>
                          </Tab>
                        </Tabs>
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>

      <EditClientModal
        show={showEditModal}
        onHide={() => setEditModal(false)}
        handleSave={handleSaveClientDetails}
        clientDetails={_.get(clientDetails, "clientDetails", "")}
      />
      <ClientNotesModal
        show={show}
        onHide={handleClose}
        loadClientNotes={loadClientNotes}
        clientName={clientFullName}
        clientId={clientId}
        notes={clientNotes}
        saveClientNote={addClientNotes}
        deleteNote={deleteClientNotes}
      />
      <ArchiveModal
        show={archiveModalShow}
        onHide={() => setArchiveModalShow(false)}
        handleConfirm={() => handleArchiveWantAd()}
      />

      <DeleteModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        title={_.get(deleteButtonProps, "title", "")}
        message={_.get(deleteButtonProps, "message", "")}
        buttonText={_.get(deleteButtonProps, "btnText", "")}
        handleConfirm={() => handleDeleteItem()}
      />
      <ClientDeleteModal
        show={deleteClientModal}
        onHide={() => setDeleteClientModal(false)}
        handleConfirm={() => handleDeleteClient()}
        clientName={clientFullName}
      />
      <PremiumPlanModal
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};

export default ViewClientDetails;
